import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table, TableBody, TableCell, TableRow } from '@shared/ui';
import type { AssetBalanceDto } from '@shared/api/contracts';
import { SINGLE_USDT_NETWORKS } from '@entities/withdrawal';
import { getPairBalanceAmount } from '../../lib/pair-balance';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';

interface BalanceSummaryPanelProps {
  assetBalances: AssetBalanceDto[];
  totalBalance?: number;
  availableBalance?: number;
}

export const BalanceSummaryPanel = ({ assetBalances, totalBalance, availableBalance }: BalanceSummaryPanelProps) => {
  const { t } = useTranslation('cabinet');
  const [expanded, setExpanded] = useState(false);

  const totalLabel =
    totalBalance != null
      ? formatLedgerAmount(totalBalance, { assetLabel: 'USDT' })
      : '—';

  return (
    <div className="rounded-lg border border-border">
      <div className="flex flex-wrap items-baseline justify-between gap-3 p-3.5">
        <div className="space-y-0.5">
          <p className="text-xs text-muted-foreground">{t('withdrawal.auto.single.balancePanel.title')}</p>
          <p className="text-xl font-semibold tabular-nums tracking-tight">
            {totalLabel}{' '}
            <span className="text-sm font-medium text-muted-foreground">USDT</span>
          </p>
          <p className="text-xs tabular-nums text-muted-foreground">
            {t('balance.available')}:{' '}
            {availableBalance == null ? t('balance.amountUnknown') : formatLedgerAmount(availableBalance, { assetLabel: 'USDT' })}
          </p>
        </div>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-xs text-muted-foreground"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded
            ? t('withdrawal.auto.single.balancePanel.hide')
            : t('withdrawal.auto.single.balancePanel.show')}
        </Button>
      </div>

      {expanded && (
        <Table>
          <TableBody>
            {SINGLE_USDT_NETWORKS.map((network) => {
              const amount = getPairBalanceAmount(assetBalances, 'USDT', network);
              return (
                <TableRow key={network} className="border-t border-border">
                  <TableCell className="py-2 text-sm text-muted-foreground">
                    USDT <span className="text-muted-foreground/80">· {network}</span>
                  </TableCell>
                  <TableCell className="py-2 text-right text-sm tabular-nums">
                    {formatLedgerAmount(amount, { assetLabel: 'USDT' })} USDT
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
